import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Circle } from 'lucide-react';
const symptoms = ['Rigidez en las articulaciones, especialmente al despertar.', 'Hinchazón abdominal persistente después de comer.', 'Niebla mental o dificultad para concentrarse.', 'Fatiga constante, incluso después de dormir 8 horas.', 'Problemas en la piel (sequedad extrema, enrojecimiento).', 'Dificultad para perder peso, especialmente en el abdomen.', 'Antojos incontrolables de dulces o carbohidratos.'];
export function Chapter1() {
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const toggleCheck = (index: number) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedItems(newChecked);
  };
  return <section id="capitulo-1" className="min-h-screen py-24 px-6 lg:px-16 bg-cream relative" data-id="element-44">
      <div className="max-w-3xl mx-auto" data-id="element-45">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-46">
          <h2 className="text-gold font-medium tracking-widest uppercase text-sm mb-4" data-id="element-47">
            Capítulo 1
          </h2>
          <h3 className="font-serif text-4xl lg:text-5xl text-darkBrown font-bold mb-10 leading-tight" data-id="element-48">
            El Fuego Interno
          </h3>
        </motion.div>

        <div className="space-y-8 text-body text-darkBrown/80" data-id="element-49">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-50">
            Imagina que tienes una pequeña herida en el dedo. Se pone roja, se
            hincha y duele un poco. Esa es la inflamación aguda, una respuesta
            saludable y necesaria de tu sistema inmunológico para sanar. Pero,
            ¿qué pasa cuando esa respuesta de alarma nunca se apaga?
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-51">
            La <strong data-id="element-52">inflamación crónica sistémica</strong> es como un fuego
            lento y silencioso que arde dentro de tu cuerpo 24/7. No la ves,
            pero la sientes. A medida que envejecemos, nuestras células se
            vuelven menos eficientes para limpiar los desechos metabólicos, un
            proceso conocido como "inflammaging" (inflamación + envejecimiento).
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white p-8 lg:p-10 rounded-3xl shadow-sm border border-gold/10 my-12" data-id="element-53">
            <h4 className="font-serif text-2xl text-darkBrown font-bold mb-2" data-id="element-54">
              Autodiagnóstico
            </h4>
            <p className="text-darkBrown/60 text-base mb-8" data-id="element-55">
              Marca los síntomas que has experimentado en las últimas dos
              semanas. Si marcas 3 o más, tu cuerpo está pidiendo ayuda para
              desinflamarse.
            </p>

            <div className="space-y-4" data-id="element-56">
              {symptoms.map((symptom, index) => {
              const isChecked = checkedItems.has(index);
              return <button key={index} onClick={() => toggleCheck(index)} className={`w-full flex items-start gap-4 p-4 rounded-xl transition-all text-left ${isChecked ? 'bg-lightGold border-gold/30' : 'bg-cream/30 hover:bg-cream border-transparent'} border`} data-id="element-57">
                    <div className="mt-1 flex-shrink-0" data-id="element-58">
                      {isChecked ? <CheckCircle2 className="w-6 h-6 text-gold" data-id="element-59" /> : <Circle className="w-6 h-6 text-darkBrown/30" data-id="element-60" />}
                    </div>
                    <span className={`text-lg transition-colors ${isChecked ? 'text-darkBrown font-medium' : 'text-darkBrown/70'}`} data-id="element-61">
                      {symptom}
                    </span>
                  </button>;
            })}
            </div>

            {checkedItems.size >= 3 && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-8 p-4 bg-gold/10 text-deepGold rounded-xl border border-gold/20 text-center font-medium" data-id="element-62">
                Tu cuerpo muestra signos claros de inflamación. ¡Estás en el
                lugar correcto! El protocolo de 14 días está diseñado
                exactamente para aliviar estos síntomas.
              </motion.div>}
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-63">
            La buena noticia es que nuestras células son increíblemente
            resilientes. Cuando dejamos de alimentarlas con productos que las
            agreden y comenzamos a darles los nutrientes que necesitan
            —antioxidantes, grasas saludables y fitonutrientes— el fuego
            comienza a apagarse casi de inmediato.
          </motion.p>
        </div>
      </div>
    </section>;
}